import { computed, type Ref, type ComputedRef } from 'vue'
import { usePreview } from '@/composables/usePreview'
import { partitionSeomaticScripts } from '@/utils/gtm-scripts'
import { withOpenGraphType } from '@/utils/open-graph'

interface SeomaticData {
  metaTitleContainer?: string
  metaTagContainer?: string
  metaScriptContainer?: string | object
}

/**
 * Composable for working with Seomatic data
 * 
 * @param seomaticData - Ref or ComputedRef containing seomatic data
 * @returns Computed properties for title, meta tags, and scripts
 */
export function useSeomatic(
  seomaticData: Ref<SeomaticData | null | undefined> | ComputedRef<SeomaticData | null | undefined>,
  options: { openGraphType?: 'article' | 'website' } = {},
) {
  const { isPreview } = usePreview()
  const deferredGtmContainerId = useState<string>('deferredGtmContainerId', () => '')

  // Extract title from seomatic metaTitleContainer
  const seomaticTitle = computed(() => {
    const seomatic = unref(seomaticData)
    const metaTitleContainer = seomatic?.metaTitleContainer
    if (!metaTitleContainer) return null

    // Parse HTML string to extract title text
    const titleMatch = metaTitleContainer.match(/<title[^>]*>([^<]+)<\/title>/i)
    if (titleMatch && titleMatch[1]) {
      return titleMatch[1].trim()
    }
    return null
  })

  // Parse meta tags from seomatic metaTagContainer
  const seomaticMetaTags = computed(() => {
    const seomatic = unref(seomaticData)
    const metaTagContainer = seomatic?.metaTagContainer
    if (!metaTagContainer) return []

    const metaTags = []
    
    // Match all meta tags
    const metaRegex = /<meta\s+([^>]+)>/gi
    let match
    
    while ((match = metaRegex.exec(metaTagContainer)) !== null) {
      const attributes = match[1]
      const tag: { content?: string; property?: string; name?: string } = {}
      
      // Extract name, property, content, etc.
      const nameMatch = attributes?.match(/name=["']([^"']+)["']/i)
      const propertyMatch = attributes?.match(/property=["']([^"']+)["']/i)
      const contentMatch = attributes?.match(/content=["']([^"']+)["']/i)
      
      if (contentMatch && contentMatch[1]) {
        tag.content = contentMatch[1].replace(/&amp;/g, '&').replace(/&quot;/g, '"')
      }
      
      if (propertyMatch && propertyMatch[1]) {
        // Open Graph or other property-based meta tags
        tag.property = propertyMatch[1]
      } else if (nameMatch && nameMatch[1]) {
        // Standard name-based meta tags
        tag.name = nameMatch[1]
      }
      
      if (Object.keys(tag).length > 0) {
        metaTags.push(tag)
      }
    }
    
    return metaTags
  })

  // Function to extract all attributes from script tag
  const extractScriptAttributes = (scriptTag: string) => {
    const attributes: Record<string, any> = {}
    
    // Extract the opening tag (everything between <script and >)
    // Handle both <script ...> and <script...> (with or without space)
    const tagMatch = scriptTag.match(/<script\s*([^>]*)>/i)
    if (!tagMatch || !tagMatch[1]) return attributes
    
    const tagContent = tagMatch[1].trim()
    if (!tagContent) return attributes
    
    // Try to parse non-standard format first (0='{"name":"type","value":"text\/plain"}' format)
    // Find patterns like: 0='{...}' or 0="{...}"
    // Use a more robust approach: find number=' or number=", then extract JSON until matching quote
    const nonStandardPattern = /(\d+)\s*=\s*(['"])(\{)/g
    let match
    let hasNonStandard = false
    
    while ((match = nonStandardPattern.exec(tagContent)) !== null) {
      const quoteChar = match[2] // ' or "
      const jsonStart = match.index + match[0].length - 1 // Position of {
      
      // Find the matching closing quote, accounting for escaped quotes
      let jsonEnd = jsonStart
      let depth = 0
      let inString = false
      let escaped = false
      
      for (let i = jsonStart; i < tagContent.length; i++) {
        const char = tagContent[i]
        
        if (escaped) {
          escaped = false
          continue
        }
        
        if (char === '\\') {
          escaped = true
          continue
        }
        
        if (char === '"' && !escaped) {
          inString = !inString
        } else if (!inString) {
          if (char === '{') depth++
          if (char === '}') {
            depth--
            if (depth === 0) {
              jsonEnd = i
              break
            }
          }
        }
      }
      
      // Extract JSON string
      const jsonString = tagContent.substring(jsonStart, jsonEnd + 1)
      const expectedQuote = tagContent[jsonEnd + 1]
      
      // Verify we have the matching quote
      if (expectedQuote === quoteChar) {
        try {
          const attrData = JSON.parse(jsonString)
          if (attrData && typeof attrData === 'object' && attrData.name && attrData.value !== undefined) {
            attributes[attrData.name] = attrData.value
            hasNonStandard = true
          }
        } catch (e) {
          // Not valid JSON, continue
        }
      }
    }
    
    // If we found non-standard format, return early
    if (hasNonStandard) return attributes
    
    // Parse standard HTML attributes (name="value" or name='value' or name=value)
    // Improved regex to handle quoted and unquoted values
    const standardAttrRegex = /(\S+)\s*=\s*["']([^"']+)["']|(\S+)\s*=\s*(\S+)|(\S+)(?=\s|$)/g
    let standardMatch
    
    while ((standardMatch = standardAttrRegex.exec(tagContent)) !== null) {
      const attrName = standardMatch[1] || standardMatch[3] || standardMatch[5]
      const attrValue = standardMatch[2] || standardMatch[4] || true
      
      if (attrName) {
        // Handle boolean attributes (async, defer without value)
        if (attrName === 'async' || attrName === 'defer') {
          attributes[attrName] = attrValue === true ? true : attrValue
        } else {
          attributes[attrName] = attrValue === true ? '' : attrValue
        }
      }
    }
    
    return attributes
  }

  // Parse scripts from seomatic metaScriptContainer
  const seomaticScripts = computed(() => {
    // Never inject SEOmatic's script containers in the Page Builder / Live Preview: these carry the
    // site's tracking + widgets (Google Tag Manager, Google Ads, Analytics, ChiliPiper concierge),
    // none of which should run inside the editor canvas.
    if (isPreview.value) {
      return { headScripts: [], bodyScripts: [] }
    }
    const seomatic = unref(seomaticData)
    const metaScriptContainer = seomatic?.metaScriptContainer
    if (!metaScriptContainer) {
      return { headScripts: [], bodyScripts: [] }
    }

    try {
      // Parse JSON string
      const scriptsData = typeof metaScriptContainer === 'string' 
        ? JSON.parse(metaScriptContainer) 
        : metaScriptContainer

      const headScripts: any[] = []
      const bodyScripts: any[] = []

      // Parse script field for head and body scripts
      if (scriptsData.script) {
        const scriptContent = scriptsData.script
        
        // Split by body position marker
        const bodyMarker = '<!-- Position: <body> -->'
        const parts = scriptContent.split(bodyMarker)
        
        // Head scripts (before body marker)
        if (parts[0]) {
          const headPart = parts[0].replace(/<!-- Position: <head> -->/g, '').trim()
          if (headPart) {
            // Extract script tags from head part
            const headScriptRegex = /<script[^>]*>([\s\S]*?)<\/script>/gi
            let match
            while ((match = headScriptRegex.exec(headPart)) !== null) {
              const scriptTag = match[0]

              // Extract all attributes from script tag
              const attributes = extractScriptAttributes(scriptTag)
              
              // Check if it's an external script (has src)
              const srcMatch = scriptTag.match(/src=["']([^"']+)["']/i)
              if (srcMatch) {
                const scriptObj: any = {
                  src: srcMatch[1]
                }
                
                // Add all extracted attributes
                Object.keys(attributes).forEach(key => {
                  if (key === 'async' || key === 'defer') {
                    scriptObj[key] = true
                  } else {
                    scriptObj[key] = attributes[key]
                  }
                })
                
                headScripts.push(scriptObj)
              } else {
                // Inline script - extract content and clean it
                const innerHTML = match[1]?.trim()

                if (innerHTML) {
                  const scriptObj: any = {
                    innerHTML: innerHTML
                  }
                  
                  // Add all extracted attributes
                  Object.keys(attributes).forEach(key => {
                    if (key === 'async' || key === 'defer') {
                      scriptObj[key] = true
                    } else {
                      scriptObj[key] = attributes[key]
                    }
                  })
                  
                  headScripts.push(scriptObj)
                }
              }
            }
          }
        }
        
        // Body scripts (after body marker)
        if (parts[1]) {
          const bodyPart = parts[1].trim()
          if (bodyPart) {
            // Extract script tags from body part
            const bodyScriptRegex = /<script[^>]*>([\s\S]*?)<\/script>/gi
            let match
            while ((match = bodyScriptRegex.exec(bodyPart)) !== null) {
              const scriptTag = match[0]
              
              // Extract all attributes from script tag
              const attributes = extractScriptAttributes(scriptTag)
              
              const srcMatch = scriptTag.match(/src=["']([^"']+)["']/i)
              if (srcMatch) {
                const scriptObj: any = {
                  src: srcMatch[1]
                }
                
                // Add all extracted attributes
                Object.keys(attributes).forEach(key => {
                  if (key === 'async' || key === 'defer') {
                    scriptObj[key] = true
                  } else {
                    scriptObj[key] = attributes[key]
                  }
                })
                
                bodyScripts.push(scriptObj)
              } else {
                const innerHTML = match[1]?.trim()
                if (innerHTML) {
                  const scriptObj: any = {
                    innerHTML: innerHTML
                  }
                  
                  // Add all extracted attributes
                  Object.keys(attributes).forEach(key => {
                    if (key === 'async' || key === 'defer') {
                      scriptObj[key] = true
                    } else {
                      scriptObj[key] = attributes[key]
                    }
                  })
                  
                  bodyScripts.push(scriptObj)
                }
              }
            }
            

            // Extract noscript tags
            const noscriptRegex = /<noscript>([\s\S]*?)<\/noscript>/gi
            let noscriptMatch
            while ((noscriptMatch = noscriptRegex.exec(bodyPart)) !== null) {
              bodyScripts.push({
                innerHTML: noscriptMatch[1],
                tag: 'noscript'
              })
            }
          }
        }
      }

      // Parse bodyScript field
      if (scriptsData.bodyScript) {
        const bodyScriptContent = scriptsData.bodyScript
        
        // Extract script tags
        const scriptRegex = /<script[^>]*>([\s\S]*?)<\/script>/gi
        let match
        while ((match = scriptRegex.exec(bodyScriptContent)) !== null) {
          const scriptTag = match[0]
          const srcMatch = scriptTag.match(/src=["']([^"']+)["']/i)
          if (srcMatch) {
            bodyScripts.push({
              src: srcMatch[1],
              async: scriptTag.includes('async'),
              defer: scriptTag.includes('defer')
            })
          } else {
            const innerHTML = match[1]?.trim()
            if (innerHTML) {
              bodyScripts.push({
                innerHTML: innerHTML
              })
            }
          }
        }
        
        // Extract noscript tags
        const noscriptRegex = /<noscript>([\s\S]*?)<\/noscript>/gi
        let noscriptMatch
        while ((noscriptMatch = noscriptRegex.exec(bodyScriptContent)) !== null) {
          bodyScripts.push({
            innerHTML: noscriptMatch[1],
            tag: 'noscript'
          })
        }
      }

      const { headScripts: filteredHeadScripts, bodyScripts: filteredBodyScripts, gtmContainerId } =
        partitionSeomaticScripts(headScripts, bodyScripts)

      if (gtmContainerId && !deferredGtmContainerId.value) {
        deferredGtmContainerId.value = gtmContainerId
      }

      return { headScripts: filteredHeadScripts, bodyScripts: filteredBodyScripts }
    } catch (error) {
      console.error('Error parsing metaScriptContainer:', error)
      return { headScripts: [], bodyScripts: [] }
    }
  })

  // Convert meta tags to useHead format
  const seomaticHeadMeta = computed(() => {
    const tags = seomaticMetaTags.value.map(tag => {
      if (tag.property) {
        // Open Graph or other property-based meta tags
        return {
          property: tag.property,
          content: tag.content
        }
      } else if (tag.name) {
        // Standard name-based meta tags
        return {
          name: tag.name,
          content: tag.content
        }
      }
      return null
    }).filter(Boolean)

    return options.openGraphType ? withOpenGraphType(tags, options.openGraphType) : tags
  })

  // Convert head scripts to useHead format
  const seomaticHeadScripts = computed(() => {
    if (seomaticScripts.value.headScripts.length === 0) return []
    
    return seomaticScripts.value.headScripts.map((script, index) => {
      if (script.innerHTML) {
        // Use innerHTML for inline scripts in Nuxt 3
        const scriptObj: any = {
          innerHTML: script.innerHTML,
          key: `seomatic-inline-head-script-${index}`
        }
        
        // Add all other attributes from script object
        Object.keys(script).forEach(key => {
          if (key !== 'innerHTML' && key !== 'src') {
            scriptObj[key] = script[key]
          }
        })
        
        return scriptObj
      } else if (script.src) {
        const scriptObj: any = {
          src: script.src,
          key: `seomatic-external-head-script-${index}`
        }
        
        // Add all other attributes from script object (including async, defer, type, class, data-*, etc.)
        Object.keys(script).forEach(key => {
          if (key !== 'src' && key !== 'innerHTML') {
            scriptObj[key] = script[key]
          }
        })
        
        return scriptObj
      }
      return null
    }).filter(Boolean)
  })

  // Convert body scripts to useHead format
  const seomaticBodyScripts = computed(() => {
    if (seomaticScripts.value.bodyScripts.length === 0) {
      return {}
    }

    const bodyScripts: any[] = []
    const noscriptTags: any[] = []
    
    seomaticScripts.value.bodyScripts.forEach((script, index) => {
      if (script.tag === 'noscript') {
        const noscriptObj: any = {
          tag: 'noscript',
          innerHTML: script.innerHTML,
          key: `seomatic-body-noscript-${index}`
        }
        
        // Add all other attributes from script object
        Object.keys(script).forEach(key => {
          if (key !== 'tag' && key !== 'innerHTML' && key !== 'src') {
            noscriptObj[key] = script[key]
          }
        })
        
        noscriptTags.push(noscriptObj)
      } else if (script.innerHTML) {
        const scriptObj: any = {
          innerHTML: script.innerHTML,
          body: true,
          key: `seomatic-inline-body-script-${index}`
        }
        
        // Add all other attributes from script object
        Object.keys(script).forEach(key => {
          if (key !== 'innerHTML' && key !== 'src' && key !== 'body') {
            scriptObj[key] = script[key]
          }
        })
        
        bodyScripts.push(scriptObj)
      } else if (script.src) {
        const scriptObj: any = {
          src: script.src,
          body: true,
          key: `seomatic-external-body-script-${index}`
        }
        
        // Add all other attributes from script object (including async, defer, type, class, data-*, etc.)
        Object.keys(script).forEach(key => {
          if (key !== 'src' && key !== 'innerHTML' && key !== 'body') {
            scriptObj[key] = script[key]
          }
        })
        
        bodyScripts.push(scriptObj)
      }
    })
    
    const result: any = {}
    if (bodyScripts.length > 0) {
      result.script = bodyScripts
    }
    if (noscriptTags.length > 0) {
      result.noscript = noscriptTags
    }
    
    return result
  })

  return {
    seomaticTitle,
    seomaticMetaTags,
    seomaticScripts,
    seomaticHeadMeta,
    seomaticHeadScripts,
    seomaticBodyScripts
  }
}

